import { createContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import type { AuthUser } from '../types';

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: unknown;
  logout: () => Promise<void>;
  loginUrl: string;
  googleConfigured: boolean;
  devBypassEnabled: boolean;
  devLogin: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authApi.me,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const { data: config } = useQuery({
    queryKey: ['auth', 'config'],
    queryFn: authApi.config,
    staleTime: Infinity,
  });

  const value = useMemo<AuthContextValue>(
    () => ({
      user: data ?? null,
      isLoading,
      isAuthenticated: Boolean(data),
      error,
      loginUrl: authApi.googleLoginUrl(),
      googleConfigured: config?.googleConfigured ?? true,
      devBypassEnabled: config?.devBypassEnabled ?? false,
      devLogin: async () => {
        const user = await authApi.devLogin();
        queryClient.setQueryData(['auth', 'me'], user);
        window.location.assign('/');
      },
      logout: async () => {
        await authApi.logout();
        queryClient.setQueryData(['auth', 'me'], null);
        window.location.assign('/login');
      },
    }),
    [data, isLoading, error, config, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
